/**
 * Risk Score Dashboard
 * ====================
 * Main dashboard component showing overall risk assessment.
 * Designed to be "first thing compliance sees" above the fold.
 *
 * Features:
 * - Overall risk score with visual gauge
 * - 4 component breakdown (Geographic, Product, Channel, Customer)
 * - Jurisdiction comparison (declared vs IP-detected)
 * - Top risk drivers with evidence
 * - Graceful handling of missing/partial data
 */

import React from 'react';
import {
  Shield,
  ShieldAlert,
  ShieldCheck,
  Clock,
  AlertTriangle,
  CheckCircle,
  RotateCw,
} from 'lucide-react';

import { RiskComponentChart } from './RiskComponentChart';
import { JurisdictionCard } from './JurisdictionCard';
import { RiskDriversList } from './RiskDriversList';
import { useRiskBreakdown } from '../../hooks/useRiskBreakdown';

// Risk level configuration
const RISK_LEVEL_CONFIG = {
  low: {
    color: '#10b981',
    bg: 'rgba(16, 185, 129, 0.15)',
    border: 'rgba(16, 185, 129, 0.3)',
    icon: ShieldCheck,
    label: 'Low Risk',
  },
  medium: {
    color: '#f59e0b',
    bg: 'rgba(245, 158, 11, 0.15)',
    border: 'rgba(245, 158, 11, 0.3)',
    icon: Shield,
    label: 'Medium Risk',
  },
  high: {
    color: '#ef4444',
    bg: 'rgba(239, 68, 68, 0.15)',
    border: 'rgba(239, 68, 68, 0.3)',
    icon: ShieldAlert,
    label: 'High Risk',
  },
  critical: {
    color: '#dc2626',
    bg: 'rgba(220, 38, 38, 0.2)',
    border: 'rgba(220, 38, 38, 0.4)',
    icon: ShieldAlert,
    label: 'Critical',
  },
};

// Recommended action configuration
const ACTION_CONFIG = {
  auto_approve: {
    color: '#10b981',
    label: 'Auto Approve',
    description: 'Low risk - can be auto-approved',
  },
  manual_review: {
    color: '#f59e0b',
    label: 'Manual Review',
    description: 'Requires compliance review',
  },
  auto_reject: {
    color: '#ef4444',
    label: 'Auto Reject',
    description: 'High risk - requires escalation',
  },
};

/**
 * Risk score gauge component
 */
function RiskScoreGauge({ score, level }) {
  const config = RISK_LEVEL_CONFIG[level] || RISK_LEVEL_CONFIG.medium;
  const Icon = config.icon;
  const fillPercent = Math.min(100, Math.max(0, score));

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
      background: config.bg,
      borderRadius: '12px',
      border: `2px solid ${config.border}`,
      minWidth: '120px',
    }}>
      {/* Score */}
      <div style={{
        fontSize: '36px',
        fontWeight: 700,
        color: config.color,
        lineHeight: 1,
      }}>
        {score}
      </div>

      {/* Label */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        marginTop: '4px',
        fontSize: '13px',
        fontWeight: 600,
        color: config.color,
        textTransform: 'uppercase',
      }}>
        <Icon size={14} />
        {config.label}
      </div>

      {/* Progress bar */}
      <div style={{
        width: '100%',
        height: '6px',
        background: 'rgba(255,255,255,0.2)',
        borderRadius: '3px',
        marginTop: '12px',
        overflow: 'hidden',
      }}>
        <div style={{
          height: '100%',
          width: `${fillPercent}%`,
          background: config.color,
          borderRadius: '3px',
          transition: 'width 0.3s ease',
        }} />
      </div>
    </div>
  );
}

/**
 * Action button based on recommended action
 */
function ActionButton({ action, onClick }) {
  const config = ACTION_CONFIG[action] || ACTION_CONFIG.manual_review;

  const buttonStyle = action === 'auto_approve'
    ? {
        background: 'var(--success)',
        color: 'white',
      }
    : action === 'auto_reject'
    ? {
        background: 'var(--danger)',
        color: 'white',
      }
    : {
        background: 'var(--bg-tertiary)',
        color: 'var(--text-primary)',
        border: '1px solid var(--border-color)',
      };

  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        padding: '10px 16px',
        borderRadius: '8px',
        fontSize: '13px',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        border: 'none',
        width: '100%',
        marginTop: '12px',
        ...buttonStyle,
      }}
    >
      {action === 'auto_approve' && <CheckCircle size={14} />}
      {action === 'manual_review' && <Clock size={14} />}
      {action === 'auto_reject' && <AlertTriangle size={14} />}
      {config.label}
    </button>
  );
}

/**
 * Loading state
 */
function LoadingState() {
  return (
    <div style={{
      background: 'var(--bg-secondary)',
      borderRadius: '12px',
      padding: '24px',
      border: '1px solid var(--border-color)',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        color: 'var(--text-muted)',
      }}>
        <RotateCw size={20} className="animate-spin" style={{ animation: 'spin 1s linear infinite' }} />
        <span>Loading risk assessment...</span>
      </div>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

/**
 * Error state
 */
function ErrorState({ error, onRetry }) {
  return (
    <div style={{
      background: 'rgba(239, 68, 68, 0.08)',
      borderRadius: '12px',
      padding: '24px',
      border: '1px solid rgba(239, 68, 68, 0.2)',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        color: '#ef4444',
      }}>
        <AlertTriangle size={20} />
        <div>
          <div style={{ fontWeight: 600 }}>Failed to load risk assessment</div>
          <div style={{ fontSize: '12px', marginTop: '4px' }}>
            {error?.message || 'Unknown error'}
          </div>
        </div>
        {onRetry && (
          <button
            onClick={onRetry}
            style={{
              marginLeft: 'auto',
              padding: '6px 12px',
              borderRadius: '6px',
              border: '1px solid #ef4444',
              background: 'transparent',
              color: '#ef4444',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 500,
            }}
          >
            Retry
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Format relative time
 */
function formatRelativeTime(dateString) {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

/**
 * Main Risk Score Dashboard
 */
export function RiskScoreDashboard({ applicantId, onActionClick }) {
  const {
    data: breakdown,
    isLoading,
    error,
    refetch,
  } = useRiskBreakdown(applicantId);

  if (isLoading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={refetch} />;
  }

  if (!breakdown) {
    return null;
  }

  const {
    overall_score = 0,
    overall_level = 'medium',
    recommended_action = 'manual_review',
    components = [],
    jurisdiction = {},
    top_drivers = [],
    data_completeness = 100,
    missing_data = [],
    last_calculated_at,
  } = breakdown;

  const levelConfig = RISK_LEVEL_CONFIG[overall_level] || RISK_LEVEL_CONFIG.medium;

  return (
    <div style={{
      background: 'var(--bg-secondary)',
      borderRadius: '12px',
      padding: '20px',
      border: `1px solid ${levelConfig.border}`,
      marginBottom: '20px',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <Shield size={16} style={{ color: levelConfig.color }} />
          <span style={{
            fontSize: '13px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: 'var(--text-primary)',
          }}>
            Risk Assessment
          </span>

          {data_completeness < 100 && (
            <span style={{
              fontSize: '10px',
              padding: '2px 6px',
              borderRadius: '4px',
              background: 'rgba(245, 158, 11, 0.15)',
              color: '#f59e0b',
              fontWeight: 500,
            }}>
              {data_completeness}% Complete
            </span>
          )}
        </div>

        <span style={{
          fontSize: '11px',
          color: 'var(--text-muted)',
        }}>
          Updated {formatRelativeTime(last_calculated_at)}
        </span>
      </div>

      {/* Main content grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '140px 1fr',
        gap: '20px',
      }}>
        {/* Left: Score gauge + action */}
        <div>
          <RiskScoreGauge score={overall_score} level={overall_level} />
          <ActionButton
            action={recommended_action}
            onClick={() => onActionClick?.(recommended_action)}
          />
        </div>

        {/* Right: Component breakdown */}
        <RiskComponentChart
          components={components}
          dataCompleteness={data_completeness}
          missingData={missing_data}
        />
      </div>

      {/* Bottom row: Jurisdiction + Drivers */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '16px',
        marginTop: '16px',
      }}>
        <JurisdictionCard jurisdiction={jurisdiction} />
        <RiskDriversList drivers={top_drivers} />
      </div>
    </div>
  );
}

export default RiskScoreDashboard;
